import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";

import MediaValetApi from "../api/MediaValet";
import { isAutodeskAsset } from "../util";
import {
  isMsFile,
  isRendition,
  isRequiredRendition,
  isXLTFile,
} from "../types";

export enum GalleryState {
  EXTERNAL_SHARED = "ExternalShared",
  REQUESTED = "Requested",
}

export interface IAssetGallery {
  id: string;
  assets: Asset[];
  sharedAt: string;
  sharedBy: {
    name: string;
    id: string;
    email: string;
    organization: string;
  };
  state: GalleryState;
}

interface IWebGalleryStoreConfiguration {
  api: MediaValetApi;
  logoSrc?: string;
  features: { [key: string]: string };
}

export enum ZoomLevels {
  LEVEL_75 = 75,
  LEVEL_100 = 100,
  LEVEL_150 = 150,
  LEVEL_200 = 200,
  LEVEL_250 = 250,
  LEVEL_300 = 300,
  LEVEL_350 = 350,
  LEVEL_400 = 400,
  LEVEL_FULL = 500,
}

export interface IWebGalleryStore {
  readonly shouldPoll: boolean;

  fetchGalleryById(id: string): Promise<IAssetGallery | undefined>;
  assets: Asset[];
  gallery: IAssetGallery | undefined;
  staleAssets: boolean;
  selectedAsset?: Asset | undefined;
  selectedAssetIndex: number;
  selectAssetById(id: string): Promise<Asset | undefined>;
  hasNextAsset: boolean;
  getNextAsset(): Asset | undefined;
  hasPreviousAsset: boolean;
  getPreviousAsset(): Asset | undefined;
  zoomLevel?: ZoomLevels;
  logoSrc?: string;
  features?: { [key: string]: string };
}

const DEFAULT_CONFIGURATION = {
  api: new MediaValetApi(),
  logoSrc: undefined,
  features: {},
};

const MAX_NUMBER_OF_ASSETS_FOR_POLLING = 100;
const MAX_NUMBER_OF_RENDITIONS_ASSETS_FOR_POLLING = 5;

class WebGalleryStore implements IWebGalleryStore {
  gallery: IAssetGallery | undefined;
  assets: Asset[];
  staleAssets: boolean;
  selectedAssetIndex: number;
  selectedZoomIndex: number;
  api: MediaValetApi;
  readonly zoomLevels: ZoomLevels[];
  readonly logoSrc?: string;
  readonly features?: { [key: string]: string };

  get canPollMultipageAssets() {
    return (
      this.assets.filter((asset) => isRendition(asset))?.length <
      MAX_NUMBER_OF_RENDITIONS_ASSETS_FOR_POLLING
    );
  }

  get shouldPoll() {
    return (
      (this.gallery?.state === GalleryState.REQUESTED || this.staleAssets) &&
      this.assets.length < MAX_NUMBER_OF_ASSETS_FOR_POLLING &&
      this.canPollMultipageAssets
    );
  }

  get selectedAsset() {
    if (
      this.selectedAssetIndex < 0 ||
      this.selectedAssetIndex >= this.assets.length
    ) {
      // TODO: selectedAsset can be undefined
      // @ts-ignore
      return undefined;
    }

    return this.assets[this.selectedAssetIndex];
  }

  get hasNextAsset() {
    return this.selectedAssetIndex < this.assets.length - 1;
  }

  get hasPreviousAsset() {
    return this.selectedAssetIndex > 0;
  }

  constructor(config: IWebGalleryStoreConfiguration = DEFAULT_CONFIGURATION) {
    this.gallery = undefined;
    this.api = config.api;
    this.assets = [];
    this.staleAssets = false;
    this.selectedAssetIndex = -1;
    this.logoSrc = config.logoSrc;
    this.features = config.features;
    this.zoomLevels = [
      ZoomLevels.LEVEL_75,
      ZoomLevels.LEVEL_100,
      ZoomLevels.LEVEL_150,
      ZoomLevels.LEVEL_200,
      ZoomLevels.LEVEL_250,
      ZoomLevels.LEVEL_300,
      ZoomLevels.LEVEL_350,
      ZoomLevels.LEVEL_400,
      ZoomLevels.LEVEL_FULL,
    ];
    this.selectedZoomIndex = this.zoomLevels.indexOf(ZoomLevels.LEVEL_200);

    makeObservable(this, {
      gallery: observable,
      api: observable.deep,
      assets: observable,
      selectedZoomIndex: observable,
      selectedAssetIndex: observable,
      selectedAsset: computed,
      hasNextAsset: computed,
      hasPreviousAsset: computed,
      zoomLevel: computed,
      canZoomIn: computed,
      canZoomOut: computed,
      zoomIn: action.bound,
      zoomOut: action.bound,
      getNextAsset: action.bound,
      getPreviousAsset: action.bound,
      selectAssetById: action.bound,
    });
  }

  async fetchGalleryById(id: string) {
    // initial zoom level needs be calculated once data is loaded
    // if it is initial request or if there were no assets to show prior to reload
    let recalculateZoom = false;
    if (!this.gallery || this.assets.length === 0) {
      recalculateZoom = true;
    }

    this.gallery = await this.api.fetchGalleryById(id);
    const assets = await this.api.fetchGalleryAssetsById(id);

    const mappedAssets = assets.assets.map(async (asset) => {
      if (!isXLTFile(asset) && isMsFile(asset)) {
        const response = await this.api.fetchWopiPayload(asset.id, id);
        return {
          ...asset,
          wopiPayload: response,
        };
      }
      if (isRendition(asset)) {
        const response = await this.api.fetchRenditionKinds(id);
        const renditionKinds = response.filter(isRequiredRendition);
        try {
          const renditions = await Promise.all(
            renditionKinds.map(async (renditionKind) => {
              const rendition = await this.api.fetchRendition(
                id,
                asset.id,
                renditionKind.renditionKindId
              );
              return {
                ...rendition,
                ...renditionKind,
              };
            })
          );
          return {
            ...asset,
            renditions,
          };
        } catch {
          return {
            ...asset,
            renditionsFailed: true,
          };
        }
      }
      return asset;
    });

    this.assets = (
      (await Promise.all(mappedAssets)) as IRenditionAsset[]
    ).filter((x) => !x.renditionsFailed);
    this.staleAssets = assets.staleAssets;

    if (this.gallery) {
      this.gallery.id = id.split("_")[1];
    }

    if (recalculateZoom) {
      this.setInitialZoomLevel();
    }

    return this.gallery;
  }

  async selectAssetById(id: string) {
    if (
      this.assets.length > 0 &&
      !this.assets.some((asset) => asset.id === id)
    ) {
      throw new Error("Asset not found.  ID may be incorrect.");
    }

    this.selectedAssetIndex = this.assets.findIndex((asset) => asset.id === id);

    if (this.selectedAsset && this.gallery) {
      if (isAutodeskAsset(this.selectedAsset)) {
        const token = await this.api.fetchAutodeskToken();
        runInAction(() => {
          if (this.selectedAsset) {
            this.selectedAsset.autodeskPayload = token;
          }
        });
      }
    }

    return this.selectedAsset;
  }

  getNextAsset() {
    if (!this.hasNextAsset) {
      return undefined;
    }

    return this.assets[this.selectedAssetIndex + 1];
  }

  getPreviousAsset() {
    if (!this.hasPreviousAsset) {
      return undefined;
    }

    return this.assets[this.selectedAssetIndex - 1];
  }

  get zoomLevel() {
    if (
      this.selectedZoomIndex < 0 ||
      this.selectedZoomIndex >= this.zoomLevels.length
    ) {
      return undefined;
    }

    return this.zoomLevels[this.selectedZoomIndex];
  }

  get canZoomIn() {
    return this.selectedZoomIndex < this.zoomLevels.length - 1;
  }

  setInitialZoomLevel() {
    // When 1 asset is shared, display with “Full” zoom
    // When 2 assets are shared, display with zoom at 350%
    // When 3 assets are shared, display with zoom at 250%
    // When 4 or more assets are shared, display with zoom at 200%
    // See issue PRM-233 for more details
    if (this.assets.length === 1) {
      this.selectedZoomIndex = this.zoomLevels.indexOf(ZoomLevels.LEVEL_FULL);
    }
    if (this.assets.length === 2) {
      this.selectedZoomIndex = this.zoomLevels.indexOf(ZoomLevels.LEVEL_350);
    }
    if (this.assets.length === 3) {
      this.selectedZoomIndex = this.zoomLevels.indexOf(ZoomLevels.LEVEL_250);
    }
    if (this.assets.length > 3) {
      this.selectedZoomIndex = this.zoomLevels.indexOf(ZoomLevels.LEVEL_200);
    }
  }

  zoomIn() {
    if (this.canZoomIn) {
      this.selectedZoomIndex += 1;
    }
    return this.zoomLevel;
  }

  get canZoomOut() {
    return this.selectedZoomIndex > 0;
  }

  zoomOut() {
    if (this.canZoomOut) {
      this.selectedZoomIndex -= 1;
    }
    return this.zoomLevel;
  }
}

export default WebGalleryStore;
